/* eslint-disable no-unused-vars */
import {createSlice} from '@reduxjs/toolkit';
import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import {flushTemplateCache} from '@components/AI/aiSlice';
import {VISIBILITY_ACCOUNT} from '@constants/index';
import {recipientApi} from '@api/recipient/api';
import {getErrorCode, getErrorMessage} from '@utils/requests/error';
import {documentApi} from '@api/document/api';
import {reminderApi} from '@api/reminder/api';
import {auditApi} from '@api/audit/api';
import {transactionApi} from '@api/transaction/api';
import {userApi} from '@api/user/api';
import {getApprovalsAndFieldsForDocument, getApprovalsAndFieldsForTransaction} from '@components/Packages/utils/getApprovalsAndFields';
import {selectActiveAccountId} from '@components/Login/userSlice';
import {selectCurrentUserEmails} from '@utils/currentUserEmails';
import {getOwnerRecipientToCreate} from '@utils/getOwnerRecipientToCreate';
// eslint-disable-next-line import/no-cycle
import {deleteFetchDocumentEque} from '../Designer/designerSlice';

const TEMPLATE_STORE_PATH = 'template.template';

export const templateSlice = createSlice({
    name: 'template',
    initialState: {
        template: {},
        approvals: {},
        fields: {},
        //
        schedule: null,
        isUpdatingSchedule: false,
        isFetchingSchedule: false,
        scheduleError: null,
        //
        templateError: null,
        isTemplateFetching: false,
        isCreatingTemplate: false,
        creatingTemplateError: null,
        uploadErrors: null,
        // documents
        isDocumentsFetching: false,
        documentsFetchError: null,
        isUploadingDocuments: false,
        uploadDocumentsError: null,
        isUpdatingDocuments: false,
        updateDocumentsError: null,
        isUpdatingDocument: false,
        updateDocumentError: null,
        isDeletingDocument: false,
        deleteDocumentError: null,
        // settings
        isUpdatingSettings: false,
        updateSettingsError: null,
        audits: {},
        isFetchingAudits: false,
        rolesForLoadingPlaceholders: null,
        isFetchingApprovalsAndFields: false,
        isSenderVisibleForLoadingPlaceholders: null
    },
    reducers: {
        setTemplate: (state, action) => {
            state.template = action.payload;
        },
        setDocuments: (state, action) => {
            state.template.documents = action.payload;
        },
        setRecipients: (state, action) => {
            state.template.recipients = action.payload;
        },
        setTemplateError: (state, action) => {
            state.templateError = action.payload;
        },
        setTemplateResponseStatusCode: (state, action) => {
            state.templateResponseStatusCode = action.payload;
        },
        setIsTemplateFetching: (state, action) => {
            state.isTemplateFetching = action.payload;
        },
        setIsCreatingTemplate: (state, action) => {
            state.isCreatingTemplate = action.payload;
        },
        setCreatingTemplateError: (state, action) => {
            state.creatingTemplateError = action.payload;
        },
        setUploadErrors: (state, action) => {
            state.uploadErrors = action.payload;
        },
        // documents
        setIsDocumentsFetching: (state, action) => {
            state.isDocumentsFetching = action.payload;
        },
        setDocumentsFetchError: (state, action) => {
            state.documentsFetchError = action.payload;
        },
        setTransactionError: (state, action) => {
            state.transactionError = action.payload;
        },
        setIsUploadingDocuments: (state, action) => {
            state.isUploadingDocuments = action.payload;
        },
        setUploadDocumentsError: (state, action) => {
            state.uploadDocumentsError = action.payload;
        },
        setIsUpdatingDocuments: (state, action) => {
            state.isUpdatingDocuments = action.payload;
        },
        setUpdateDocumentsError: (state, action) => {
            state.updateDocumentsError = action.payload;
        },
        setIsUpdatingDocument: (state, action) => {
            state.isUpdatingDocument = action.payload;
        },
        setUpdateDocumentError: (state, action) => {
            state.updateDocumentError = action.payload;
        },
        setIsDeletingDocument: (state, action) => {
            state.isDeletingDocument = action.payload;
        },
        setDeleteDocumentError: (state, action) => {
            state.deleteDocumentError = action.payload;
        },
        //
        setSchedule: (state, action) => {
            state.schedule = action.payload;
        },
        setIsFetchingSchedule: (state, action) => {
            state.isFetchingSchedule = action.payload;
        },
        setIsUpdatingSchedule: (state, action) => {
            state.isUpdatingSchedule = action.payload;
        },
        setScheduleError: (state, action) => {
            state.scheduleError = action.payload;
        },
        //
        setIsUpdatingSettings: (state, action) => {
            state.isUpdatingSettings = action.payload;
        },
        setUpdateSettingsError: (state, action) => {
            state.updateSettingsError = action.payload;
        },
        setAudits: (state, action) => {
            state.audits = {
                ...state.audits,
                ...action.payload
            };
        },
        setIsFetchingAudits: (state, action) => {
            state.isFetchingAudits = action.payload;
        },
        setRolesForLoadingPlaceholders: (state, action) => {
            state.rolesForLoadingPlaceholders = action.payload;
        },
        setIsFetchingApprovalsAndFields: (state, action) => {
            state.isFetchingApprovalsAndFields = action.payload;
        },
        setIsSenderVisibleForLoadingPlaceholders: (state, action) => {
            state.isSenderVisibleForLoadingPlaceholders = action.payload;
        },
        setApprovalsForDocument: (state, action) => {
            const {documentId, approvals} = action.payload;
            state.approvals[documentId] = approvals;
        },
        setFieldsForApproval: (state, action) => {
            const {approvalId, fields} = action.payload;
            state.fields[approvalId] = fields;
        },
        addApproval: (state, action) => {
            const {documentId, approval} = action.payload;
            const list = state.approvals[documentId] || [];
            state.approvals[documentId] = [...list, approval];
        },
        removeApproval: (state, action) => {
            const {documentId, approvalId} = action.payload;
            const list = state.approvals[documentId] || [];
            state.approvals[documentId] = list.filter((a) => a.id !== approvalId);
            delete state.fields[approvalId];
        },
        updateApprovalInState: (state, action) => {
            const {documentId, approval} = action.payload;
            const list = state.approvals[documentId] || [];
            state.approvals[documentId] = list.map((a) => (a.id === approval.id ? approval : a));
        },
        addField: (state, action) => {
            const {approvalId, field} = action.payload;
            const list = state.fields[approvalId] || [];
            state.fields[approvalId] = [...list, field];
        },
        removeField: (state, action) => {
            const {approvalId, fieldId} = action.payload;
            const list = state.fields[approvalId] || [];
            state.fields[approvalId] = list.filter((f) => f.id !== fieldId);
        },
        updateFieldInState: (state, action) => {
            const {approvalId, field} = action.payload;
            const list = state.fields[approvalId] || [];
            state.fields[approvalId] = list.map((f) => (f.id === field.id ? {...f, ...field} : f));
        },
        clearApprovalsForDocument: (state, action) => {
            const {documentId} = action.payload;
            const approvalsList = state.approvals[documentId] || [];
            approvalsList.forEach((a) => { delete state.fields[a.id]; });
            state.approvals[documentId] = [];
        }
    }
});
export const {
    setTemplate,
    setIsTemplateFetching,
    setTemplateError,
    setTemplateResponseStatusCode,
    setIsCreatingTemplate,
    setCreatingTemplateError,
    setUploadErrors,
    setRecipients,
    // documents
    setDocuments,
    setIsDocumentsFetching,
    setDocumentsFetchError,
    setIsUploadingDocuments,
    setUploadDocumentsError,
    setIsUpdatingDocuments,
    setUpdateDocumentsError,
    setIsUpdatingDocument,
    setUpdateDocumentError,
    setIsDeletingDocument,
    setDeleteDocumentError,
    // reminder schedule
    setSchedule,
    setIsFetchingSchedule,
    setIsUpdatingSchedule,
    setScheduleError,
    //
    setIsUpdatingSettings,
    setUpdateSettingsError,
    setAudits,
    setIsFetchingAudits,
    setRolesForLoadingPlaceholders,
    setIsFetchingApprovalsAndFields,
    setIsSenderVisibleForLoadingPlaceholders,
    setApprovalsForDocument,
    setFieldsForApproval,
    addApproval,
    removeApproval,
    updateApprovalInState,
    addField,
    removeField,
    updateFieldInState,
    clearApprovalsForDocument
} = templateSlice.actions;

export const getIsTemplateInProgress = () => (dispatch, getState) => {
    const {
        isUpdatingSchedule,
        isTemplateFetching,
        isCreatingTemplate,
        isDocumentsFetching,
        isUploadingDocuments,
        isUpdatingDocuments,
        isUpdatingDocument,
        isDeletingDocument,
        isUpdatingSettings
    } = getState().template;

    const isInProgress = isUpdatingSchedule
        || isTemplateFetching
        || isCreatingTemplate
        || isDocumentsFetching
        || isUploadingDocuments
        || isUpdatingDocuments
        || isUpdatingDocument
        || isDeletingDocument
        || isUpdatingSettings;
    return isInProgress;
};

export const resetTemplateErrors = () => async (dispatch) => {
    dispatch(setTemplateError(null));
    dispatch(setTemplateResponseStatusCode(null));
    dispatch(setDocumentsFetchError(null));
    dispatch(setUploadDocumentsError(null));
    dispatch(setUpdateDocumentsError(null));
    dispatch(setUpdateDocumentError(null));
    dispatch(setDeleteDocumentError(null));
    dispatch(setUpdateSettingsError(null));
    dispatch(setScheduleError(null));
};

export const fetchTemplate = ({
    headers, params, options: fetchOptions = {}
} = {}) => async (dispatch, getState) => {
    const {packageId} = params;
    const {user: userState} = getState();
    const accountId = selectActiveAccountId(getState()) || get(userState, 'account.id');
    const apiOptions = {};
    if (headers) apiOptions.headers = headers;
    if (fetchOptions.timeout) apiOptions.timeout = fetchOptions.timeout;

    dispatch(setIsTemplateFetching(true));
    dispatch(setTemplate({}));
    dispatch(setTemplateError(null));
    dispatch(setTemplateResponseStatusCode(null));

    try {
        const [templateResult, documentsResult, recipientsResult] = await Promise.all([
            transactionApi.getTemplate({accountId, templateId: packageId, ...apiOptions}),
            documentApi.getAllDocuments(packageId, undefined, apiOptions),
            recipientApi.getAllRecipients(packageId, undefined, apiOptions)
        ]);

        const scheduleSettled = await reminderApi.getSchedules(packageId, apiOptions)
            .then((value) => ({status: 'fulfilled', value}))
            .catch(() => ({status: 'rejected'}));
        const scheduleRaw = scheduleSettled.status === 'fulfilled'
            ? scheduleSettled.value.data
            : null;
        const schedule = Array.isArray(scheduleRaw) ? scheduleRaw[0] || null : scheduleRaw || null;

        const documents = documentsResult;
        const {approvalsByDocumentId, fieldsByApprovalId} = await getApprovalsAndFieldsForTransaction(
            packageId,
            documents.map((doc) => doc.id),
            apiOptions
        );

        Object.entries(approvalsByDocumentId).forEach(([documentId, approvals]) => {
            dispatch(setApprovalsForDocument({documentId, approvals}));
        });
        Object.entries(fieldsByApprovalId).forEach(([approvalId, fields]) => {
            dispatch(setFieldsForApproval({approvalId, fields}));
        });

        const template = {
            ...templateResult.data,
            documents,
            recipients: recipientsResult,
            schedule
        };

        dispatch(setTemplate(template));
        dispatch(setSchedule(schedule));
        dispatch(setIsTemplateFetching(false));
        dispatch(setTemplateError(null));
        dispatch(setTemplateResponseStatusCode(200));

        return template;
    } catch (err) {
        dispatch(setIsTemplateFetching(false));
        dispatch(setTemplateError(getErrorMessage(err)));
        dispatch(setTemplateResponseStatusCode(getErrorCode(err)));

        return false;
    }
};

function getTemplateRequestPayload({templatePackage, locale, timezoneId, packageSettings}) {
    const senderVisible = !!templatePackage?.data?.senderVisible;
    const visibility = templatePackage?.visibility === VISIBILITY_ACCOUNT ? 'public' : 'sender';
    const settings = {
        ...packageSettings,
        ...templatePackage?.settings,
        owner_is_recipient: String(senderVisible)
    };
    // `ceremony` is frontend-only UI state; the backend rejects unknown setting keys.
    delete settings.ceremony;

    const daysToExpire = Number(templatePackage?.days_to_expire) || null;

    return {
        name: templatePackage?.name || '',
        visibility,
        language: templatePackage?.language || locale,
        timezone: templatePackage?.timezone || timezoneId,
        origin: templatePackage?.data?.origin || 'validsign',
        autocomplete: templatePackage?.autocomplete ?? true,
        days_to_expire: daysToExpire,
        expires_at: daysToExpire ? null : (templatePackage?.expires_at ?? templatePackage?.due ?? null),
        message_to_recipients: templatePackage?.message_to_recipients || '',
        settings
    };
}

async function ensureOwnerRecipient({transactionId, currentUser, currentUserEmails}) {
    if (!transactionId || !currentUser?.email) {
        return;
    }
    try {
        const existing = await recipientApi.getAllRecipients(transactionId).catch(() => []);
        const data = getOwnerRecipientToCreate(existing, currentUser, currentUserEmails);
        if (!data) {
            return;
        }
        await recipientApi.createRecipient(transactionId, data);
    } catch (err) {
        // non-fatal
    }
}

async function uploadTemplateDocumentsBatch({templateId, documents}) {
    if (isEmpty(documents) || !templateId) {
        return [];
    }
    const formData = new FormData();
    const payloads = documents.map((doc, index) => ({
        name: doc.name,
        order: index
    }));
    formData.append('payloads', new Blob([JSON.stringify(payloads)], {type: 'application/json'}));
    documents.forEach((doc) => {
        formData.append('file', doc);
    });
    try {
        await documentApi.createDocuments(templateId, formData);
        return [];
    } catch (err) {
        return [getErrorMessage(err)];
    }
}

export const createTemplate = ({
    templatePackage, template, documents
}) => async (dispatch, getState) => {
    const {app, user, dashboard} = getState();
    const accountId = selectActiveAccountId(getState()) || get(user, 'account.id');
    const userId = get(user, 'user.id');

    dispatch(setIsCreatingTemplate(true));
    dispatch(setCreatingTemplateError(null));

    try {
        const requestPayload = getTemplateRequestPayload({
            templatePackage,
            locale: app.locale,
            timezoneId: user.user.timezoneId,
            packageSettings: dashboard.packageSettings
        });

        let templateId;
        if (!isEmpty(template)) {
            const cloneResult = await transactionApi.cloneAsTemplate({
                accountId,
                templateId: template.id,
                data: {name: requestPayload.name}
            });
            templateId = cloneResult.data?.id || null;
            if (templateId) {
                await transactionApi.updateTemplate({
                    accountId,
                    userId,
                    templateId,
                    data: {visibility: requestPayload.visibility}
                });
            }
        } else {
            const createResult = await transactionApi.createTemplate({
                accountId,
                userId,
                data: requestPayload
            });
            templateId = createResult.data?.id || null;
        }

        await ensureOwnerRecipient({
            transactionId: templateId,
            currentUser: user.user,
            currentUserEmails: selectCurrentUserEmails(getState())
        });
        const uploadErrors = await uploadTemplateDocumentsBatch({templateId, documents});
        dispatch(setUploadErrors(uploadErrors));

        dispatch(setIsCreatingTemplate(false));
        dispatch(setCreatingTemplateError(null));
        dispatch(flushTemplateCache({templateId}));

        return templateId;
    } catch (err) {
        dispatch(setIsCreatingTemplate(false));
        dispatch(setCreatingTemplateError(getErrorMessage(err)));
        return false;
    }
};

const UPDATE_TEMPLATE_ALLOWED_FIELDS = [
    'name',
    'visibility',
    'description',
    'language',
    'timezone',
    'origin',
    'message_to_recipients',
    'autocomplete',
    'retain_until',
    'expires_at',
    'days_to_expire',
    'attributes'
];
const UPDATE_TEMPLATE_ALLOWED_SETTINGS = [
    'show_decline_button',
    'disable_download_incomplete_transaction',
    'handover_auto_redirect',
    'handover_parameters',
    'hide_capture_text',
    'hide_watermark',
    'face_to_face',
    'left_menu_expand',
    'enforce_capture_signature',
    'font_size',
    'geo_location',
    'show_in_iframe',
    'owner_is_recipient'
];
const UPDATE_TEMPLATE_SETTINGS_PREFIXES = [
    'handover_complete_url_',
    'handover_text_',
    'handover_title_'
];

function getIsSettingAllowed(key) {
    return UPDATE_TEMPLATE_ALLOWED_SETTINGS.includes(key)
        || UPDATE_TEMPLATE_SETTINGS_PREFIXES.some((prefix) => key.startsWith(prefix));
}

function getUpdateTemplatePayload(payload) {
    const cleanPayload = UPDATE_TEMPLATE_ALLOWED_FIELDS.reduce((acc, key) => {
        if (payload[key] !== undefined) acc[key] = payload[key];
        return acc;
    }, {});
    // Backend rejects both expiry fields at once; keep them mutually exclusive.
    if (Number(cleanPayload.days_to_expire) > 0) {
        cleanPayload.expires_at = null;
    } else {
        cleanPayload.days_to_expire = null;
    }
    if (payload.settings) {
        const cleanSettings = Object.keys(payload.settings).reduce((acc, key) => {
            if (getIsSettingAllowed(key)) acc[key] = payload.settings[key];
            return acc;
        }, {});
        if (!isEmpty(cleanSettings)) cleanPayload.settings = cleanSettings;
    }
    return cleanPayload;
}

export const updateTemplate = (payload) => async (dispatch, getState) => {
    const state = getState();
    const packageData = get(state, TEMPLATE_STORE_PATH);
    const accountId = selectActiveAccountId(state) || get(state, 'user.account.id');
    const userId = get(state, 'user.user.id');

    try {
        const data = getUpdateTemplatePayload(payload);
        const result = await transactionApi.updateTemplate({
            accountId,
            userId,
            templateId: packageData.id,
            data
        });
        dispatch(setTemplate({
            ...packageData,
            ...result.data,
            days_to_expire: data.days_to_expire,
            settings: {...packageData.settings, ...result.data?.settings}
        }));
        dispatch(flushTemplateCache({templateId: packageData.id}));
        return true;
    } catch (err) {
        return false;
    }
};
// documents

export const refreshDocuments = () => async (dispatch, getState) => {
    const packageData = get(getState(), TEMPLATE_STORE_PATH);

    if (!packageData.id) {
        return false;
    }

    dispatch(setIsDocumentsFetching(true));
    dispatch(setDocumentsFetchError(null));

    try {
        const documents = await documentApi.getAllDocuments(packageData.id);
        const existingDocs = get(getState(), 'template.template.documents', []);
        const mergedDocs = documents.map((doc) => {
            const existing = existingDocs.find((d) => d.id === doc.id);
            return existing ? {...doc, approvals: existing.approvals || []} : doc;
        });
        dispatch(setDocuments(mergedDocs));
        dispatch(setIsDocumentsFetching(false));
        dispatch(setDocumentsFetchError(null));
        dispatch(deleteFetchDocumentEque());

        return true;
    } catch (err) {
        dispatch(setIsDocumentsFetching(false));
        dispatch(setDocumentsFetchError(getErrorMessage(err)));
        return false;
    }
};
export const fetchDocument = (packageId, documentId) => async (dispatch, getState) => {
    const template = get(getState(), 'template.template');

    try {
        const [documentResult, {approvalsByDocumentId, fieldsByApprovalId}] = await Promise.all([
            documentApi.getDocument(packageId, documentId),
            getApprovalsAndFieldsForDocument(packageId, documentId)
        ]);
        dispatch(setApprovalsForDocument({documentId, approvals: approvalsByDocumentId[documentId]}));
        Object.entries(fieldsByApprovalId).forEach(([approvalId, fields]) => {
            dispatch(setFieldsForApproval({approvalId, fields}));
        });

        dispatch(setTemplate({
            ...template,
            documents: template.documents.map((document) => (
                document.id === documentId ? documentResult.data : document
            ))
        }));
        return true;
    } catch (err) {
        return false;
    }
};

export const fetchApprovalsAndFields = (packageId) => async (dispatch, getState) => {
    const documents = get(getState(), 'template.template.documents', []);

    if (!packageId || !documents.length) {
        return;
    }

    dispatch(setIsFetchingApprovalsAndFields(true));

    try {
        const {approvalsByDocumentId, fieldsByApprovalId} = await getApprovalsAndFieldsForTransaction(
            packageId,
            documents.map((doc) => doc.id)
        );

        Object.entries(approvalsByDocumentId).forEach(([documentId, approvals]) => {
            dispatch(setApprovalsForDocument({documentId, approvals}));
        });
        Object.entries(fieldsByApprovalId).forEach(([approvalId, fields]) => {
            dispatch(setFieldsForApproval({approvalId, fields}));
        });
    } finally {
        dispatch(setIsFetchingApprovalsAndFields(false));
    }
};

export const refreshRecipients = () => async (dispatch, getState) => {
    const packageData = get(getState(), TEMPLATE_STORE_PATH);

    if (!packageData.id) {
        return false;
    }

    try {
        const result = await recipientApi.getAllRecipients(packageData.id);
        dispatch(setRecipients(result));
        return result;
    } catch (err) {
        return false;
    }
};

/**
 * Refetches the template itself together with its recipients.
 * Needed after a document upload: the backend may add a recipient from a text-tag
 * or flag the owner as a recipient through `settings.owner_is_recipient`.
 */
export const refreshTemplateAndRecipients = () => async (dispatch, getState) => {
    const templateId = get(getState(), `${TEMPLATE_STORE_PATH}.id`);
    const accountId = selectActiveAccountId(getState()) || get(getState(), 'user.account.id');

    if (!templateId) {
        return false;
    }

    try {
        const [templateResult, recipients] = await Promise.all([
            transactionApi.getTemplate({accountId, templateId}),
            recipientApi.getAllRecipients(templateId)
        ]);
        const currentTemplate = get(getState(), TEMPLATE_STORE_PATH, {});

        dispatch(setTemplate({
            ...currentTemplate,
            ...templateResult.data,
            recipients
        }));
        return true;
    } catch (err) {
        return false;
    }
};

export const handleFetchTemplate = () => async (dispatch, getState) => {
    const packageId = get(getState(), 'template.template.id');

    if (!packageId) {
        return false;
    }

    try {
        const template = await dispatch(fetchTemplate({
            params: {packageId}
        }));

        if (!template) {
            return false;
        }

        return {recipients: template.recipients, documents: template.documents};
    } catch (err) {
        dispatch(setTemplateError(getErrorMessage(err)));
        return false;
    }
};

export const uploadDocuments = ({
    packageId, documents
}) => async (dispatch, getState) => {
    dispatch(setIsUploadingDocuments(true));
    dispatch(setUploadDocumentsError(null));

    try {
        const formData = new FormData();
        const payloads = documents.map((doc, index) => ({
            name: doc.name || doc.file?.name,
            order: doc.order ?? index,
            ...(doc.deprecated_id && {deprecated_id: doc.deprecated_id}),
            ...(doc.data && {data: doc.data})
        }));
        formData.append('payloads', new Blob([JSON.stringify(payloads)], {type: 'application/json'}));
        documents.forEach((doc) => {
            formData.append('file', doc.file || doc);
        });

        const result = await documentApi.createDocuments(packageId, formData);
        const uploadedDocuments = result.data;
        const existingDocuments = get(getState(), 'template.template.documents', []);
        dispatch(setDocuments([...existingDocuments, ...uploadedDocuments]));
        dispatch(fetchApprovalsAndFields(packageId));
        await dispatch(refreshTemplateAndRecipients());
        dispatch(setIsUploadingDocuments(false));
        dispatch(flushTemplateCache({templateId: packageId}));
        return !isEmpty(uploadedDocuments) ? uploadedDocuments : null;
    } catch (err) {
        dispatch(setIsUploadingDocuments(false));
        dispatch(setUploadDocumentsError(getErrorMessage(err)));
        return null;
    }
};

export const updateDocuments = ({
    packageId, documents
}) => async (dispatch) => {
    dispatch(setIsUpdatingDocuments(true));
    dispatch(setUpdateDocumentsError(null));

    try {
        await Promise.all(
            documents.map((doc) => documentApi.updateDocument(packageId, doc.id, {order: doc.order}))
        );
        await dispatch(refreshDocuments());
        dispatch(setIsUpdatingDocuments(false));
        dispatch(flushTemplateCache({templateId: packageId}));
        return true;
    } catch (err) {
        dispatch(setUpdateDocumentsError(getErrorMessage(err)));
        dispatch(setIsUpdatingDocuments(false));
        return false;
    }
};

export const updateDocument = ({
    packageId, document
}) => async (dispatch) => {
    dispatch(setIsUpdatingDocument(true));
    dispatch(setUpdateDocumentError(null));

    try {
        await documentApi.updateDocument(packageId, document.id, document);
        await dispatch(refreshDocuments());
        dispatch(setIsUpdatingDocument(false));
        dispatch(flushTemplateCache({templateId: packageId}));
        return true;
    } catch (err) {
        dispatch(setIsUpdatingDocument(false));
        dispatch(setUpdateDocumentError('esl.feedback.document_reorder.error'));
        return false;
    }
};

export const deleteDocument = ({
    packageId, documentId, callback
}) => async (dispatch, getState) => {
    const packageData = get(getState(), TEMPLATE_STORE_PATH);

    dispatch(setIsDeletingDocument(true));
    dispatch(setDeleteDocumentError(null));

    try {
        await documentApi.deleteDocument(packageId, documentId);
        dispatch(setTemplate({
            ...packageData,
            documents: packageData.documents.filter(({id}) => id !== documentId)
        }));
        dispatch(fetchApprovalsAndFields(packageId));
        dispatch(setIsDeletingDocument(false));
        dispatch(flushTemplateCache({templateId: packageId}));
        if (callback) {
            callback();
        }
        return true;
    } catch (err) {
        dispatch(setIsDeletingDocument(false));
        dispatch(setDeleteDocumentError(getErrorMessage(err)));
        return false;
    }
};

//

export const fetchSchedule = ({
    headers, baseUrl, params, options = {}
} = {}) => async (dispatch) => {
    const {packageId} = params;
    const requestOptions = {};
    if (headers) requestOptions.headers = headers;
    if (options.timeout) requestOptions.timeout = options.timeout;
    if (baseUrl) requestOptions.baseUrl = baseUrl;

    dispatch(setIsFetchingSchedule(true));
    try {
        const result = await reminderApi.getSchedules(packageId, requestOptions);
        const rawData = result.data;
        const schedule = Array.isArray(rawData) ? rawData[0] || null : rawData || null;
        dispatch(setSchedule(schedule));
        return schedule;
    } catch (err) {
        dispatch(setScheduleError(getErrorMessage(err)));
        return null;
    } finally {
        dispatch(setIsFetchingSchedule(false));
    }
};

export const upsertSchedule = (packageId, scheduleData) => async (dispatch, getState) => {
    const templateId = packageId || get(getState(), `${TEMPLATE_STORE_PATH}.id`);
    dispatch(setIsUpdatingSchedule(true));
    dispatch(setScheduleError(null));
    try {
        const result = await reminderApi.createOrUpdateSchedule(templateId, scheduleData);
        dispatch(setSchedule(result.data));
        return true;
    } catch (err) {
        dispatch(setScheduleError(getErrorMessage(err)));
        return false;
    } finally {
        dispatch(setIsUpdatingSchedule(false));
    }
};

export const removeSchedule = () => async (dispatch, getState) => {
    const templateId = get(getState(), `${TEMPLATE_STORE_PATH}.id`);
    dispatch(setIsUpdatingSchedule(true));
    dispatch(setScheduleError(null));
    try {
        await reminderApi.deleteSchedule(templateId);
        dispatch(setSchedule(null));
        return true;
    } catch (err) {
        dispatch(setScheduleError(getErrorMessage(err)));
        return false;
    } finally {
        dispatch(setIsUpdatingSchedule(false));
    }
};

export const updateSettings = (payload) => async (dispatch, getState) => {
    const state = getState();
    const packageData = get(state, TEMPLATE_STORE_PATH);
    const accountId = selectActiveAccountId(state) || get(state, 'user.account.id');
    const userId = get(state, 'user.user.id');

    dispatch(setIsUpdatingSettings(true));
    dispatch(setUpdateSettingsError(null));

    try {
        const data = getUpdateTemplatePayload(payload);
        const result = await transactionApi.updateTemplate({
            accountId,
            userId,
            templateId: packageData.id,
            data
        });
        dispatch(setTemplate({
            ...packageData,
            ...result.data,
            days_to_expire: data.days_to_expire,
            settings: {...packageData.settings, ...result.data?.settings}
        }));
        dispatch(setIsUpdatingSettings(false));
        dispatch(flushTemplateCache({templateId: packageData.id}));

        return true;
    } catch (err) {
        dispatch(setUpdateSettingsError(getErrorMessage(err)));
        dispatch(setIsUpdatingSettings(false));

        return false;
    }
};

export const fetchAudits = (packageId) => async (dispatch) => {
    dispatch(setIsFetchingAudits(true));
    try {
        const result = await auditApi.getAudits(packageId, {per_page: 100});
        const audits = Array.isArray(result.data) ? result.data : get(result, 'data.data', []);
        dispatch(setAudits({
            [packageId]: audits
        }));
        return true;
    } catch (err) {
        return false;
    } finally {
        dispatch(setIsFetchingAudits(false));
    }
};

export const selectTemplate = (state) => state.template.template;
export const selectRecipients = (state) => state.template.template.recipients || [];
export const selectIsTemplateFetching = (state) => state.template.isTemplateFetching;
export const selectTemplateError = (state) => state.template.templateError;
export const selectTemplateResponseStatusCode = (state) => state.template.templateResponseStatusCode;
export const selectSchedule = (state) => state.template.schedule;
export const selectIsCreatingTemplate = (state) => state.template.isCreatingTemplate;
export const selectCreatingTemplateError = (state) => state.template.creatingTemplateError;
export const selectIsUpdatingSchedule = (state) => state.template.isUpdatingSchedule;
export const selectScheduleError = (state) => state.template.scheduleError;
export const selectIsFetchingSchedule = (state) => state.template.isFetchingSchedule;
export const selectIsUpdatingSettings = (state) => state.template.isUpdatingSettings;
export const selectUpdateSettingsError = (state) => state.template.updateSettingsError;
export const selectUpdateDocumentError = (state) => state.template.updateDocumentError;
export const selectIsUploadingDocuments = (state) => state.template.isUploadingDocuments;
export const selectIsUpdatingDocuments = (state) => state.template.isUpdatingDocuments;
export const selectIsUpdatingDocument = (state) => state.template.isUpdatingDocument;
export const selectAudits = (state) => state.template.audits;
export const selectRolesForLoadingPlaceholders = (state) => state.template.rolesForLoadingPlaceholders;
export const selectIsSenderVisibleForLoadingPlaceholders = (state) => state.transaction.isSenderVisibleForLoadingPlaceholders;

export const selectApprovals = (state) => state.template.approvals;
export const selectFields = (state) => state.template.fields;
export const selectApprovalsByDocumentId = (documentId) => (state) => state.template.approvals[documentId] || [];
export const selectFieldsByApprovalId = (approvalId) => (state) => state.template.fields[approvalId] || [];
export const selectIsFetchingApprovalsAndFields = (state) => state.template.isFetchingApprovalsAndFields;

export default templateSlice.reducer;
